// react
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

// router
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextUser } from '../../../App.context';
import { ContextProject, ContextUserProjectPermissions } from '../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2/ProjectV2.context';

// hooks
import { useWorkPlan } from '../../../hooks/useWorkPlans';

// types
import type { Activity } from '../../../@types';

// components
import { PageDetailSkeleton } from '../../ui/PageDetailSkeleton/PageDetailSkeleton';
import { DialogAddCondition } from '../../dialogs/DialogAddCondition/DialogAddCondition';
import { DialogAssignActivities } from '../../dialogs/DialogAssignActivities/DialogAssignActivities';
import { DialogAuditTimeline } from '../../dialogs/DialogAuditTimeline/DialogAuditTimeline';
import { ActivityGroupedList } from '../../ui/ActivityGroupedList/ActivityGroupedList';
import { CardDetail } from '../../cards/CardDetail/CardDetail';
import {
	getStatusColor,
	getStatusLabel,
	HeaderDetailPage,
} from '../../ui/HeaderDetailPage/HeaderDetailPage';
import { customFieldsToStats } from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';
import { DialogActions } from '../../dialogs/DialogActions/DialogActions';

// ui
import { BtnSplit } from '../../ui/BtnSplit/BtnSplit';

// mui
import {
	Alert,
	alpha,
	Box,
	Chip,
	Divider,
	Grid,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	AddCircleOutline as IconAddActivity,
	Edit as IconEdit,
	History as IconHistory,
	Assignment as IconWorkPlan,
	GppGood as IconGate,
	Undo as IconReturn,
	Send as IconSubmit,
	Share as IconInherited,
	ThumbUp as IconApprove,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { getProjectLocalCurrency } from '../../../utils/localCurrency';
import { resolveWorkPlanFxRate } from '../../../utils/fxRate';
import {
	canApprove as canApproveEntity,
	canEdit as canEditEntity,
	canReturn as canReturnEntity,
	canSubmit as canSubmitEntity,
	isDraft,
	isPendingApproval,
} from '../../../utils/makerChecker';
import { getContractAmountPaidReporting } from '../ProjectV2/Contracts/Contracts.utils';

// sse
import { useV2StatusListener } from '../../../utils/sse/useV2StatusListener';

// cards

// data
import { GRADIENTS } from '../../../data';

// config
import { featureFlags } from '../../../config/featureFlags';

// design system
import { radius, spacing } from '../../../utils/designSystem';

export const PageWorkPlan = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { workPlanId } = useParams<{ workPlanId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const workPlanIdNumber = Number(workPlanId);

	// State for activity selection
	const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([]);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [showAddExistingToWorkPlan, setShowAddExistingToWorkPlan] = useState(false);

	// State for approval workflow dialogs
	const [showSubmitDialog, setShowSubmitDialog] = useState(false);
	const [showApproveDialog, setShowApproveDialog] = useState(false);
	const [showReturnDialog, setShowReturnDialog] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Audit log dialog visibility is controlled by the `audit=1` URL param so
	// the dialog state survives reloads / sharing the link.
	const auditTimelineOpen = searchParams.get('audit') === '1';
	const setAuditTimelineOpen = (open: boolean) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (open) next.set('audit', '1');
				else next.delete('audit');
				return next;
			},
			{ replace: true }
		);
	};
	const [auditActivities, setAuditActivities] = useState<Activity[]>([]);
	const [auditLoading, setAuditLoading] = useState(false);

	// Fetch work plan by ID
	const {
		data: workPlan,
		loading: workPlanLoading,
		refetch,
		update: updateWorkPlan,
	} = useWorkPlan({
		id: workPlanIdNumber,
		isEnabled: Number.isFinite(workPlanIdNumber),
		query: { populate: 'account,loan,entity,component,category,expenseType' },
	});

	// Live updates: refetch this work plan when someone else changes its status.
	useV2StatusListener({ resource: 'workplan', id: workPlanIdNumber, onChange: refetch });

	// Track dialog state for refetch on close
	const dialogParam = searchParams.get('dialog');
	const prevDialogRef = useRef<string | null>(dialogParam);

	// Refetch data when dialog closes (dialog param goes from value to null)
	useEffect(() => {
		if (prevDialogRef.current && !dialogParam) {
			// Dialog was open and is now closed - refetch data
			refetch();
		}
		prevDialogRef.current = dialogParam;
	}, [dialogParam, refetch]);

	// Fetch audit log activities for this work plan only.
	const fetchAuditActivities = useCallback(async () => {
		if (!Number.isFinite(workPlanIdNumber)) return;
		setAuditLoading(true);
		try {
			const accessToken = sessionStorage.getItem('accessToken');
			const idToken = sessionStorage.getItem('idToken');
			const res = await fetch(
				`${process.env.REACT_APP_API_URL}/activities?workPlan=${workPlanIdNumber}&populate=actor&order=DESC&orderBy=created&limit=100&page=1`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			if (res.ok) {
				const json = await res.json();
				setAuditActivities(json.data || []);
			}
		} catch (err) {
			console.error('Failed to fetch work plan audit activities:', err);
		} finally {
			setAuditLoading(false);
		}
	}, [workPlanIdNumber]);

	useEffect(() => {
		fetchAuditActivities();
	}, [fetchAuditActivities, workPlan?.status]);

	// context
	const { user } = useContext(ContextUser);
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);
	const {
		activities,
		conditions,
		getActivitiesByWorkPlan,
		getContractsByActivity,
		updateActivity,
	} = useContext(ContextSpendPlan);

	// State for saving activities
	const [isSaving, setIsSaving] = useState(false);

	// State for assign-existing-activities dialog
	const [assignTarget, setAssignTarget] = useState<{
		id: number;
		name: string;
		field: 'componentId' | 'categoryId' | 'expenseTypeId';
	} | null>(null);

	// handlers
	const handleEdit = () => {
		if (!workPlan) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'editWorkPlan');
		params.set('dialogWorkPlan', String(workPlan.id));
		setSearchParams(params);
	};

	// Approval workflow handlers
	const handleSubmitForApproval = async () => {
		setIsSubmitting(true);
		try {
			await updateWorkPlan({ status: 'SUBMITTED' });
			setShowSubmitDialog(false);
		} catch (err) {
			console.error('Failed to submit work plan:', err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleApprove = async () => {
		setIsSubmitting(true);
		try {
			await updateWorkPlan({ status: 'ACTIVE' });
			setShowApproveDialog(false);
		} catch (err) {
			console.error('Failed to approve work plan:', err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReturn = async () => {
		setIsSubmitting(true);
		try {
			await updateWorkPlan({ status: 'DRAFT', rejectionReason: rejectionReason || null });
			setShowReturnDialog(false);
			setRejectionReason('');
		} catch (err) {
			console.error('Failed to return work plan:', err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAddActivity = () => {
		if (!workPlan) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'addActivity');
		params.set('dialogWorkPlan', String(workPlan.id));
		setSearchParams(params);
	};

	const handleOpenActivityPage = (activityId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/activity/${activityId}`);
	};

	const handleAddCondition = () => {
		if (!workPlan) return;
		const params = new URLSearchParams(searchParams);
		params.set('dialog', 'addCondition');
		params.set('dialogWorkPlan', String(workPlan.id));
		setSearchParams(params);
	};

	const handleEditCondition = (conditionId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/condition/${conditionId}`);
	};

	const handleConfirmAddActivities = async () => {
		if (selectedActivityIds.length === 0 || !workPlan) return;

		setShowConfirmDialog(false);
		setIsSaving(true);

		try {
			// Update each selected activity to assign it to this work plan
			const updatePromises = selectedActivityIds.map((activityId) =>
				updateActivity(activityId, { workPlan: workPlan.id })
			);

			await Promise.all(updatePromises);

			// Clear selection and refetch data
			setSelectedActivityIds([]);
			refetch();
		} catch (err) {
			console.error('Failed to add activities to work plan:', err);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancelAddActivities = () => {
		setShowConfirmDialog(false);
	};

	// Not found state (only after loading is complete)
	if (!workPlanLoading && !workPlan) {
		return (
			<Box sx={{ px: 3, py: 4 }}>
				<Typography>Work Plan not found</Typography>
			</Box>
		);
	}

	// computed - get activities for this work plan (safe for loading state)
	const workPlanActivities = workPlan ? getActivitiesByWorkPlan(workPlan.id) : [];

	// computed - get activities NOT on this work plan (available to add)
	const availableActivities = activities.filter((a) => {
		const activityWorkPlanId = typeof a.workPlan === 'number' ? a.workPlan : a.workPlan?.id;
		// Activity is available if it has no work plan assigned
		return !activityWorkPlanId;
	});

	// computed - get all contracts for these activities
	const workPlanContracts = workPlanActivities.flatMap((a) => getContractsByActivity(a.id));

	// computed - get work plan-level base conditions (gates/prerequisites)
	// Exclude conditions that also have a contract link (those are payment conditions, not prerequisites)
	const workPlanConditions = conditions.filter((c) => {
		if (!workPlan) return false;
		const condWorkPlan = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
		const condContract = typeof c.contract === 'number' ? c.contract : c.contract?.id;
		return condWorkPlan === workPlan.id && !condContract;
	});

	// computed - calculate amounts
	// Committed = sum of activity amounts (what's been allocated to activities)
	const committedAmount = workPlanActivities.reduce((sum, a) => sum + (a.amount || 0), 0);
	const paidAmount = workPlanContracts.reduce(
		(sum, c) => sum + getContractAmountPaidReporting(c),
		0
	);
	const workPlanAmount = workPlan?.amount || 0;

	// Local-currency basis: the work plan owns a nominal local budget. The
	// canonical resolver returns the implied rate (amountLocal / amount) plus
	// the source tag used by the local-equivalent tooltip.
	const projectLocalCurrency = getProjectLocalCurrency(project);
	const workPlanLocalCurrency = workPlan?.currencyLocal || projectLocalCurrency;
	const workPlanFx = resolveWorkPlanFxRate(workPlan, project);

	const totalConditions = workPlanConditions.length;

	// Build stats for HeaderDetailPage (only when work plan is loaded)
	const wpStatusColors = workPlan ? getStatusColor(workPlan.status) : null;

	const headerStats = workPlan
		? [
				// Status
				{
					label: t('status'),
					value: (
						<Chip
							label={getStatusLabel(workPlan.status, t)}
							size="small"
							sx={{
								bgcolor: wpStatusColors?.bg,
								color: wpStatusColors?.text,
								fontSize: { xs: '1rem', sm: '1.125rem' },
								fontWeight: 600,
								height: 28,
								px: 1,
							}}
						/>
					),
				},
				// Planning Period
				...(workPlan.dateStart && workPlan.dateEnd
					? [
							{
								label: t('spendPlan.workPlan.planningPeriod'),
								value: `${new Date(workPlan.dateStart).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })} – ${new Date(workPlan.dateEnd).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })}`,
							},
						]
					: []),
				// Custom fields
				...customFieldsToStats(
					(project?.customFieldDefinitions || []).find((d) => d.entityType === 'work_plan')
						?.definitions || [],
					workPlan.customData || {}
				),
			]
		: [];

	return (
		<Box>
			{/* Header - Full width, always visible with loading state */}
			<HeaderDetailPage
				loading={workPlanLoading}
				loadingActions={1}
				showFinancialSummary={true}
				icon={IconWorkPlan}
				breadcrumb={{
					label: 'Work Planning',
					to: `/project/${project?.idWbg}/spend-plans/work-planning`,
				}}
				title={workPlan?.name || ''}
				variant="workPlan"
				stats={headerStats}
				customAction={
					!workPlanLoading && workPlan
						? (() => {
								const gateInput = {
									status: workPlan.status,
									permissions: userProjectPermissions,
									resource: 'workPlan' as const,
									makerUserId: workPlan.submittedBy,
									currentUserId: user?.id ?? null,
								};
								// Activity composition is allowed while the work plan is
								// still being authored (DRAFT) — once SUBMITTED or beyond,
								// the membership is locked along with everything else.
								const canComposeActivities = isDraft(workPlan.status);

								return (
									<BtnSplit
										actions={[
											...(canEditEntity(gateInput)
												? [{ children: t('edit'), onClick: handleEdit, startIcon: <IconEdit /> }]
												: []),
											...(canComposeActivities
												? [
														{
															children: t('spendPlan.addActivity'),
															onClick: () => handleAddActivity(),
															startIcon: <IconAdd />,
														},
														{
															children: t('spendPlan.activities.addExistingActivity'),
															onClick: () => setShowAddExistingToWorkPlan(true),
															disabled: availableActivities.length === 0,
															startIcon: <IconAddActivity />,
														},
													]
												: []),
											...(canSubmitEntity(gateInput)
												? [
														{
															children: t('spendPlan.workPlan.submitForApproval'),
															onClick: () => setShowSubmitDialog(true),
															startIcon: <IconSubmit />,
														},
													]
												: []),
											...(canReturnEntity(gateInput)
												? [
														{
															children: t('spendPlan.workPlan.return'),
															onClick: () => setShowReturnDialog(true),
															startIcon: <IconReturn />,
														},
													]
												: []),
											...(canApproveEntity(gateInput)
												? [
														{
															children: t('approve'),
															onClick: () => setShowApproveDialog(true),
															startIcon: <IconApprove />,
														},
													]
												: []),
											{
												children: t('spendPlan.workPlan.viewAuditLog'),
												onClick: () => setAuditTimelineOpen(true),
												startIcon: <IconHistory />,
											},
										]}
									/>
								);
							})()
						: undefined
				}
				financialSummary={
					workPlan
						? {
								data: {
									currency: workPlan.currency || 'USD',
									currencyLocal: workPlanLocalCurrency,
									fx: workPlanFx,
									planned: workPlanAmount,
									committed: committedAmount,
									spent: paidAmount,
								},
							}
						: undefined
				}
			/>

			{/* Content with padding */}
			<Box>
				{/* Show skeleton for content while loading */}
				{workPlanLoading ? (
					<PageDetailSkeleton leftCards={3} rightCards={2} />
				) : workPlan ? (
					<>
						{/* Under Review Banner */}
						{isPendingApproval(workPlan.status) && (
							<Alert severity="info" sx={{ mb: spacing.md }}>
								This work plan has been submitted for approval and is currently under review.
								Editing is disabled until the work plan is approved or returned.
							</Alert>
						)}

						{/* Main Layout: Full width */}
						<Box>
							<Grid item xs={12}>
								<Stack spacing={spacing.md}>
									{/* Prerequisites (Base Conditions) — behind feature flag */}
									{featureFlags.PREREQUISITES && (
										<CardDetail
											icon={IconGate}
											title="Prerequisites"
											subtitle={
												totalConditions > 0
													? `${totalConditions} condition${totalConditions !== 1 ? 's' : ''} \u2014 Applied to all activities in this work plan`
													: 'Conditions applied to all activities in this work plan'
											}
											variant="condition"
											themeColor={GRADIENTS.condition.icon}
											onAdd={isDraft(workPlan.status) ? handleAddCondition : undefined}
											tooltipAdd="Add Prerequisite"
											emptyMessage="No prerequisites set for this work plan"
											emptyActionLabel="Add condition">
											{totalConditions > 0 && (
												<Stack spacing={0} divider={<Divider sx={{ borderColor: '#f3f4f6' }} />}>
													{workPlanConditions.map((condition) => {
														const hasPaymentTerms =
															condition.paymentAmount !== null ||
															condition.paymentPercentage !== null;
														return (
															<Stack
																key={condition.id}
																direction="row"
																alignItems="center"
																spacing={1.5}
																onClick={() => handleEditCondition(condition.id)}
																sx={{
																	cursor: 'pointer',
																	p: spacing.sm,
																	transition: 'all 0.15s ease',
																	'&:hover': {
																		bgcolor: alpha(GRADIENTS.condition.icon, 0.06),
																	},
																}}>
																<Box
																	sx={{
																		width: 36,
																		height: 36,
																		borderRadius: '50%',
																		bgcolor: alpha(GRADIENTS.condition.icon, 0.12),
																		display: 'flex',
																		alignItems: 'center',
																		justifyContent: 'center',
																		flexShrink: 0,
																	}}>
																	<IconGate
																		sx={{ fontSize: 18, color: GRADIENTS.condition.icon }}
																	/>
																</Box>
																<Box sx={{ flex: 1, minWidth: 0 }}>
																	<Typography
																		sx={{
																			fontWeight: 600,
																			fontSize: '1.375rem',
																			color: '#111827',
																		}}
																		noWrap>
																		{condition.name}
																	</Typography>
																	{condition.description && (
																		<Typography
																			sx={{
																				color: '#6b7280',
																				fontSize: '1.25rem',
																			}}
																			noWrap>
																			{condition.description}
																		</Typography>
																	)}
																</Box>
																{hasPaymentTerms && (
																	<Tooltip title="Inherited by all contracts under this work plan">
																		<Chip
																			icon={<IconInherited sx={{ fontSize: '16px !important' }} />}
																			label={
																				condition.paymentPercentage
																					? `${condition.paymentPercentage}%`
																					: formatCurrency({
																							currency: workPlan.currency,
																							value: condition.paymentAmount || 0,
																						})
																			}
																			size="small"
																			variant="outlined"
																			sx={{
																				fontWeight: 600,
																				fontSize: '1.125rem',
																			}}
																		/>
																	</Tooltip>
																)}
															</Stack>
														);
													})}
												</Stack>
											)}
										</CardDetail>
									)}

									{/* === OLD Activities section (commented out — replaced by ActivityGroupedList) ===
									<CardDetail
										icon={IconActivity}
										title="Activities"
										subtitle={`${workPlanActivities.length} activit${workPlanActivities.length !== 1 ? 'ies' : 'y'}`}
										variant="activity"
										themeColor={GRADIENTS.activity.icon}
										emptyMessage="No activities linked to this work plan yet"
										emptyActionLabel="Add activity"
										onAdd={workPlan.status !== 'SUBMITTED' ? handleAddActivity : undefined}
										headerActions={workPlan.status !== 'SUBMITTED' ? (
											<Stack direction="row" spacing={1} alignItems="center">
												{availableActivities.length > 0 && (
													<Tooltip
														title={
															showAddActivities
																? 'Hide available activities'
																: `Add from ${availableActivities.length} available`
														}>
														<Button
															size="small"
															variant={showAddActivities ? 'contained' : 'outlined'}
															startIcon={<IconAddActivity sx={{ fontSize: 18 }} />}
															onClick={() => setShowAddActivities(!showAddActivities)}
															sx={{
																borderColor: GRADIENTS.activity.icon,
																borderRadius: '6px',
																color: showAddActivities ? '#ffffff' : GRADIENTS.activity.icon,
																bgcolor: showAddActivities
																	? GRADIENTS.activity.icon
																	: 'transparent',
																fontSize: '1.25rem',
																fontWeight: 600,
																px: 2,
																py: 0.75,
																'&:hover': {
																	bgcolor: showAddActivities
																		? GRADIENTS.activity.text
																		: alpha(GRADIENTS.activity.icon, 0.08),
																	borderColor: GRADIENTS.activity.icon,
																},
															}}>
															Add Existing
														</Button>
													</Tooltip>
												)}
												<Tooltip title="Create New Activity">
													<IconButton
														size="small"
														onClick={handleAddActivity}
														sx={{
															color: GRADIENTS.activity.icon,
															padding: '6px',
															'&:hover': {
																bgcolor: alpha(GRADIENTS.activity.icon, 0.08),
															},
														}}>
														<IconAdd sx={{ fontSize: 22 }} />
													</IconButton>
												</Tooltip>
											</Stack>
										) : undefined}>
										<CardActivities
											activities={topLevelActivities}
											allActivities={activities}
											emptyMessage="No activities linked to this work plan yet"
											emptyActionLabel="Add activity"
											getContractsByActivity={getContractsByActivity}
											onAdd={workPlan.status !== 'SUBMITTED' ? handleAddActivity : undefined}
											onOpen={handleOpenActivityPage}
											showContracts
											noWrapper
										/>
									</CardDetail>
									=== END OLD Activities section === */}
								</Stack>
							</Grid>
						</Box>
					</>
				) : null}
			</Box>

			{/* Activity Grouped List — full-width tabs */}
			{!workPlanLoading && workPlan && (
				<ActivityGroupedList
					tabs={[
	{ key: 'component', label: t('spendPlan.workPlan.byComponent') },
					{ key: 'category', label: t('spendPlan.workPlan.byCategory') },
					{ key: 'expenseType', label: t('spendPlan.workPlan.byExpenseType') },
					]}
					activities={workPlanActivities}
					onNavigateActivity={handleOpenActivityPage}
					onNavigateContract={(contractId) =>
						navigate(`/project/${project?.idWbg}/spend-plans/contract/${contractId}`)
					}
					getContractsByActivity={getContractsByActivity}
					projectLocalCurrency={projectLocalCurrency}
					onAddActivity={isDraft(workPlan.status) ? () => handleAddActivity() : undefined}
					onAddExistingToWorkPlan={
						isDraft(workPlan.status) && availableActivities.length > 0
							? () => setShowAddExistingToWorkPlan(true)
							: undefined
					}
					availableActivityCount={availableActivities.length}
					onAssignExisting={(key, itemId, itemName) => {
						const fieldMap: Record<string, 'componentId' | 'categoryId' | 'expenseTypeId'> = {
							component: 'componentId',
							category: 'categoryId',
							expenseType: 'expenseTypeId',
						};
						const field = fieldMap[key];
						if (field) {
							setAssignTarget({ id: itemId, name: itemName, field });
						}
					}}
				/>
			)}

			{/* Confirmation Dialog for Adding Activities */}
			{workPlan && (
				<>
					<DialogActions
						isOpen={showConfirmDialog}
						onClose={handleCancelAddActivities}
						maxWidth={480}
						title={{ children: 'Add Activities to Work Plan' }}
						subtitle={{
							children: `Add ${selectedActivityIds.length} activit${selectedActivityIds.length !== 1 ? 'ies' : 'y'} to "${workPlan.name}"?`,
						}}
						actions={[
							{
								children: 'Cancel',
								color: 'inherit',
								onClick: handleCancelAddActivities,
								variant: 'text',
							},
							{
								children: isSaving ? 'Adding...' : 'Add to Work Plan',
								disabled: isSaving,
								loading: isSaving,
								onClick: handleConfirmAddActivities,
								variant: 'contained',
								sx: {
									bgcolor: GRADIENTS.activity.icon,
									'&:hover': { bgcolor: GRADIENTS.activity.text },
								},
							},
						]}>
						<Box sx={{ mt: 1 }}>
							<Typography sx={{ mb: 1.5, color: '#374151', fontSize: '1.35rem' }}>
								The following activities will be added to this work plan:
							</Typography>
							<Stack spacing={1}>
								{selectedActivityIds.map((id) => {
									const activity = activities.find((a) => a.id === id);
									const entityName =
										typeof activity?.entity === 'object' ? activity.entity?.name : '';
									return (
										<Box
											key={id}
											sx={{
												p: 1.5,
												bgcolor: '#f9fafb',
												borderRadius: radius.sm,
												border: '1px solid #e5e7eb',
											}}>
											<Typography sx={{ fontWeight: 600, fontSize: '1.125rem', color: '#111827' }}>
												{activity?.name || `Activity ${id}`}
											</Typography>
											{entityName && (
												<Typography sx={{ color: '#6b7280', fontSize: '1.35rem' }}>
													{entityName}
												</Typography>
											)}
										</Box>
									);
								})}
							</Stack>
						</Box>
					</DialogActions>

					{/* Add Condition Dialog (prerequisites) */}
					{featureFlags.PREREQUISITES && workPlan && (
						<DialogAddCondition
							isOpen={searchParams.get('dialog') === 'addCondition'}
							onClose={() => {
								const params = new URLSearchParams(searchParams);
								params.delete('dialog');
								params.delete('dialogWorkPlan');
								setSearchParams(params);
							}}
							onSuccess={() => {
								const params = new URLSearchParams(searchParams);
								params.delete('dialog');
								params.delete('dialogWorkPlan');
								setSearchParams(params);
							}}
							preSelectedContext={{
								workPlanId: workPlan.id,
							}}
						/>
					)}

					{/* Submit for Approval Dialog */}
					<DialogActions
						isOpen={showSubmitDialog}
						onClose={() => setShowSubmitDialog(false)}
						maxWidth={480}
						title={{ children: t('spendPlan.workPlan.submitDialogTitle') }}
						subtitle={{
							children: t('spendPlan.workPlan.submitDialogSubtitle', { name: workPlan?.name }),
						}}
						actions={[
							{
								children: 'Cancel',
								color: 'inherit',
								onClick: () => setShowSubmitDialog(false),
								variant: 'text',
							},
							{
								children: isSubmitting ? t('spendPlan.workPlan.submitting') : t('spendPlan.workPlan.submitForApproval'),
								disabled: isSubmitting,
								loading: isSubmitting,
								onClick: handleSubmitForApproval,
								variant: 'contained',
							},
						]}>
						<Typography sx={{ mt: 1, color: '#374151', fontSize: '1.35rem' }}>
							{t('spendPlan.workPlan.submitDialogBody')}
						</Typography>
					</DialogActions>

					{/* Approve Work Plan Dialog */}
					<DialogActions
						isOpen={showApproveDialog}
						onClose={() => setShowApproveDialog(false)}
						maxWidth={480}
						title={{ children: t('spendPlan.workPlan.approveDialogTitle') }}
						subtitle={{
							children: t('spendPlan.workPlan.approveDialogSubtitle', { name: workPlan?.name }),
						}}
						actions={[
							{
								children: 'Cancel',
								color: 'inherit',
								onClick: () => setShowApproveDialog(false),
								variant: 'text',
							},
							{
								children: isSubmitting ? t('spendPlan.workPlan.approving') : t('approve'),
								color: 'success',
								disabled: isSubmitting,
								loading: isSubmitting,
								onClick: handleApprove,
								variant: 'contained',
							},
						]}
					/>

					{/* Return Work Plan Dialog */}
					<DialogActions
						isOpen={showReturnDialog}
						onClose={() => {
							setShowReturnDialog(false);
							setRejectionReason('');
						}}
						maxWidth={480}
						title={{ children: t('spendPlan.workPlan.returnDialogTitle') }}
						subtitle={{
							children: t('spendPlan.workPlan.returnDialogSubtitle', { name: workPlan?.name }),
						}}
						actions={[
							{
								children: 'Cancel',
								color: 'inherit',
								onClick: () => {
									setShowReturnDialog(false);
									setRejectionReason('');
								},
								variant: 'text',
							},
							{
								children: isSubmitting ? t('spendPlan.workPlan.returning') : t('spendPlan.workPlan.returnToDraft'),
								color: 'warning',
								disabled: isSubmitting,
								loading: isSubmitting,
								onClick: handleReturn,
								variant: 'contained',
							},
						]}>
						<TextField
							fullWidth
							label={t('spendPlan.workPlan.returnReasonLabel')}
							multiline
							rows={3}
							value={rejectionReason}
							onChange={(e) => setRejectionReason(e.target.value)}
							placeholder={t('spendPlan.workPlan.returnReasonPlaceholder')}
							sx={{ mt: 1 }}
							inputProps={{ maxLength: 2000 }}
						/>
					</DialogActions>
					{/* Assign Existing Activities Dialog */}
					<DialogAssignActivities
						activities={workPlanActivities}
						assignField={assignTarget?.field || 'componentId'}
						assignToId={assignTarget?.id || 0}
						assignToName={assignTarget?.name || ''}
						isOpen={Boolean(assignTarget)}
						onClose={() => setAssignTarget(null)}
						onSuccess={() => setAssignTarget(null)}
					/>
					{/* Add Existing Activity to Work Plan Dialog */}
					<DialogAssignActivities
						activities={activities}
						assignField="workPlan"
						assignToId={workPlan.id}
						assignToName={workPlan.name}
						isOpen={showAddExistingToWorkPlan}
						onClose={() => setShowAddExistingToWorkPlan(false)}
						onSuccess={() => {
							setShowAddExistingToWorkPlan(false);
							refetch();
						}}
					/>
				</>
			)}

			<DialogAuditTimeline
				isOpen={auditTimelineOpen}
				onClose={() => setAuditTimelineOpen(false)}
				title={t('spendPlan.workPlan.auditLogTitle')}
				activities={auditActivities}
				isLoading={auditLoading}
			/>
		</Box>
	);
};
